/**
 * The SEO toolkit, bound to this site.
 *
 * @remarks
 * The one place configuration meets `@bayudwiyansatria/seo`. Everything that declares an entity or
 * writes a head tag imports `seo` from here, so there is exactly one binding and no module has to
 * be handed a configuration object.
 *
 * **Relative imports only** — this file is reachable from `vite.config.ts` through `build/`.
 * See AGENTS.md § The Thing That Breaks Silently.
 *
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * Libraries
 */
import { createSeo } from '@bayudwiyansatria/seo'

/**
 * Config
 */
import { identityConfig } from './identity'
import { siteConfig } from './site'

/**
 * The bound toolkit.
 *
 * @example
 * ```ts
 * import { seo } from '@/config/seo'
 *
 * const page = seo.page({ path: '/pricing', name: 'Pricing' })
 * ```
 */
export const seo = createSeo({
  url: siteConfig.url,
  name: siteConfig.name,
  title: siteConfig.title,
  titleTemplate: siteConfig.titleTemplate,
  description: siteConfig.description,
  language: siteConfig.language,
  locale: siteConfig.locale,
  alternateLocales: siteConfig.alternateLocales,
  themeColor: siteConfig.themeColor,
  ogImage: siteConfig.ogImage,
  twitter: siteConfig.twitter,
  robots: { default: siteConfig.robots.default },
  /*
   * Undefined on a new site, which takes the package defaults. It exists for the fork that replaces
   * a site already publishing different `@id` fragments: renaming an anchor makes a consumer see
   * the original entity disappear and an unrelated one take its place. See siteConfig.anchors.
   */
  anchors: siteConfig.anchors,
  profiles: identityConfig.profiles
})
